/**
 * Persistence layer for campaigns and membership/assignment join tables.
 * All Supabase access for these tables lives here.
 *
 * Membership goes through the SQL functions of migration 034: `join_campaign` (the invite token),
 * `remove_member` (a GM removal or a player's own leave, atomically) and `list_members`. The
 * campaign `id` is NOT the invite token any more — `campaigns.invite_token` is (rotated on every
 * GM removal and on "Reset invite link").
 */
import { supabase } from '@/utils/supabase'
import { displayCharacterName } from '@/utils/characterName'
import type {
  Campaign,
  CampaignCharacter,
  CampaignCharacterRow,
  CampaignMemberSummary,
  CampaignRemoval,
  CharacterAssignment,
  CharRole,
  MemberRole,
  MyCampaign,
} from '@/types/db'
import type { PostgrestError } from '@supabase/supabase-js'

type Result<T> = { data: T | null; error: PostgrestError | null }

/**
 * Create a campaign and register the creator as its DM, atomically.
 *
 * Delegates to the `create_campaign` SECURITY DEFINER function (see
 * supabase/migrations/002_create_campaign_rpc.sql). Doing both inserts in one DB
 * transaction avoids the chicken-and-egg the old two-step flow hit: a direct
 * `.insert().select()` triggers the `members read` SELECT policy on the new
 * row, which the creator can't satisfy until the membership exists.
 */
export async function createCampaign(name: string, packId: string): Promise<Result<Campaign>> {
  const { data, error } = await supabase
    .rpc('create_campaign', { p_name: name, p_pack_id: packId })
    .single()
  return { data: data as Campaign | null, error }
}

/** Fetch a single campaign row (id, name, `pack_id`, …) the user can see (`members read` RLS).
 *  INC-D: the campaign's `pack_id` drives the route-level pack preload in `CampaignView`. */
export async function getCampaign(campaignId: string): Promise<Result<Campaign>> {
  const { data, error } = await supabase
    .from('campaigns')
    .select('*')
    .eq('id', campaignId)
    .maybeSingle()
  return { data: data as Campaign | null, error }
}

/** Join an existing campaign with its invite token (migration 034 `join_campaign`). */
export async function joinCampaign(token: string): Promise<{ error: PostgrestError | null }> {
  const { data: userData } = await supabase.auth.getUser()
  const userId = userData.user?.id
  if (!userId) {
    return {
      error: { message: 'Not authenticated', details: '', hint: '', code: '401' } as PostgrestError,
    }
  }
  const { error } = await supabase.rpc('join_campaign', { p_token: token })
  return { error }
}

/**
 * Campaigns the current user belongs to, including their own membership role.
 * Filtered to the caller's own `campaign_members` rows: RLS lets a member read
 * every member row of campaigns they belong to, so without this filter a
 * campaign with N members would come back N times, once per member role.
 */
export async function listMyCampaigns(): Promise<Result<MyCampaign[]>> {
  const { data: userData } = await supabase.auth.getUser()
  const userId = userData.user?.id
  if (!userId) return { data: [], error: null }

  const { data, error } = await supabase
    .from('campaign_members')
    .select('member_role, campaigns(*)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false, referencedTable: 'campaigns' })
  if (error || !data) return { data: null, error }

  const campaigns: MyCampaign[] = (
    data as unknown as Array<{ member_role: MyCampaign['member_role']; campaigns: Campaign | null }>
  )
    .filter((row) => row.campaigns && !row.campaigns.archived_at)
    .map((row) => ({ ...(row.campaigns as Campaign), member_role: row.member_role }))
  return { data: campaigns, error: null }
}

/**
 * The list order (X5, Q43): campaigns by the caller's last visit, newest first; never-visited
 * campaigns after them, in their given (creation) order.
 */
export function orderByLastVisit<T extends { id: string }>(
  campaigns: T[],
  seen: Record<string, { seenAt: string }>,
): T[] {
  // ISO strings compare as strings; sort is stable, so equal times keep their input order.
  const visited = campaigns.filter((c) => seen[c.id])
  visited.sort((a, b) => {
    const [x, y] = [seen[a.id]!.seenAt, seen[b.id]!.seenAt]
    return x === y ? 0 : x < y ? 1 : -1
  })
  return [...visited, ...campaigns.filter((c) => !seen[c.id])]
}

/** The current user's role in one campaign, or null if they are not a member. */
export async function getMyRole(campaignId: string): Promise<MemberRole | null> {
  const { data: userData } = await supabase.auth.getUser()
  const userId = userData.user?.id
  if (!userId) return null
  const { data } = await supabase
    .from('campaign_members')
    .select('member_role')
    .eq('campaign_id', campaignId)
    .eq('user_id', userId)
    .maybeSingle()
  return (data?.member_role as MemberRole) ?? null
}

/** User ids holding the DM role in one campaign ("roster read" RLS: any member). The Log trusts a
 *  `recovery` line only from these senders (X7, SPEC §3 (c′)). */
export async function listDmIds(campaignId: string): Promise<string[]> {
  const { data } = await supabase
    .from('campaign_members')
    .select('user_id')
    .eq('campaign_id', campaignId)
    .eq('member_role', 'dm')
  return (data ?? []).map((r) => r.user_id as string)
}

/** Rename a campaign. DM only, enforced by the `dm writes` RLS policy. */
export async function renameCampaign(
  campaignId: string,
  name: string,
): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase.from('campaigns').update({ name }).eq('id', campaignId)
  return { error }
}

/** Set the "GM confirms" flags (migration 026): a roll result waits in the Log instead of
 *  applying at once, per rhythm. DM only, enforced by the `dm writes` RLS policy. */
export async function setGmConfirms(
  campaignId: string,
  patch: { gm_confirms_live?: boolean; gm_confirms_async?: boolean },
): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase.from('campaigns').update(patch).eq('id', campaignId)
  return { error }
}

/** Set the posting deadline in hours (migration 033); `null` = off. DM only, via `dm writes` RLS. */
export async function setPostDeadline(
  campaignId: string,
  hours: number | null,
): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase
    .from('campaigns')
    .update({ post_deadline_hours: hours })
    .eq('id', campaignId)
  return { error }
}

/**
 * Soft-delete (archive) a campaign: first unassign every character, then stamp
 * `archived_at` (migration 016). DM only — the DM's `unassign` RLS removes ALL
 * assignment rows for the campaign and `dm writes` covers the update. Returns
 * early on the first error.
 */
export async function archiveCampaign(
  campaignId: string,
): Promise<{ error: PostgrestError | null }> {
  const { error: unassignError } = await supabase
    .from('campaign_characters')
    .delete()
    .eq('campaign_id', campaignId)
  if (unassignError) return { error: unassignError }
  const { error } = await supabase
    .from('campaigns')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', campaignId)
  return { error }
}

/**
 * Leave a campaign as a player (migration 034 `remove_member`, called with my own uid): the server
 * unassigns my characters in it and drops my membership in one transaction.
 */
export async function leaveCampaign(campaignId: string): Promise<{ error: PostgrestError | null }> {
  const { data: userData } = await supabase.auth.getUser()
  const uid = userData.user?.id
  if (!uid) {
    return {
      error: { message: 'Not authenticated', details: '', hint: '', code: '401' } as PostgrestError,
    }
  }
  return removeMember(campaignId, uid)
}

/**
 * Remove a player from a campaign, or leave it (GM only, or `userId` = my own uid). The
 * `remove_member` RPC of migration 034 does it all in one transaction: unassign that player's
 * characters, drop the membership row, and — for a GM removal — rotate the invite token and write
 * the removal notice.
 */
export async function removeMember(
  campaignId: string,
  userId: string,
): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase.rpc('remove_member', { p_campaign: campaignId, p_user: userId })
  return { error }
}

/** Reset a campaign's invite link (GM only, migration 034 `reset_invite`). Returns the new token. */
export async function resetInvite(campaignId: string): Promise<Result<string>> {
  const { data, error } = await supabase.rpc('reset_invite', { p_campaign: campaignId })
  return { data: (data as string | null) ?? null, error }
}

/** The GM's member list for a campaign (migration 034 `list_members`); empty for anyone else.
 *  `character_names` is mapped through `displayCharacterName` for display. */
export async function listMembers(campaignId: string): Promise<Result<CampaignMemberSummary[]>> {
  const { data, error } = await supabase.rpc('list_members', { p_campaign: campaignId })
  if (error || !data) return { data: null, error }
  type Row = { user_id: string; label: string; character_names: string[] | null }
  const members: CampaignMemberSummary[] = (data as Row[]).map((r) => ({
    user_id: r.user_id,
    label: r.label,
    characters: (r.character_names ?? []).map(displayCharacterName),
  }))
  return { data: members, error: null }
}

/** The removal notices naming this account (migration 034 `campaign_removals`, RLS scopes them to
 *  me). An error reads as `[]`: an unmigrated database has no table, and My Campaigns must load. */
export async function listRemovals(): Promise<CampaignRemoval[]> {
  const { data } = await supabase
    .from('campaign_removals')
    .select('campaign_id, campaign_name, removed_at')
  return (data as CampaignRemoval[] | null) ?? []
}

/** Dismiss one removal notice (RLS scopes the delete to my own row). */
export async function dismissRemoval(
  campaignId: string,
): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase
    .from('campaign_removals')
    .delete()
    .eq('campaign_id', campaignId)
  return { error }
}

/**
 * Attach a character to a campaign in a given role.
 *
 * Same-pack precondition (ADR-066): a character may only join a campaign that
 * runs the same content pack. This is the client-side guard giving a clean
 * error before the write; the DB trigger `campaign_characters_same_pack`
 * (migration 018) is the authoritative backstop. INC-D adds the roster-level
 * filter that hides cross-pack characters from the assign affordance entirely.
 */
export async function assignCharacter(
  campaignId: string,
  characterId: string,
  charRole: CharRole,
): Promise<Result<CampaignCharacter>> {
  const [{ data: campaign }, { data: character }] = await Promise.all([
    supabase.from('campaigns').select('pack_id').eq('id', campaignId).maybeSingle(),
    supabase.from('characters').select('pack_id').eq('id', characterId).maybeSingle(),
  ])
  if (campaign && character && campaign.pack_id !== character.pack_id) {
    return {
      data: null,
      error: {
        message: `Pack mismatch: this character (${character.pack_id}) cannot join a ${campaign.pack_id} campaign.`,
        details: '',
        hint: '',
        code: '23514',
      } as PostgrestError,
    }
  }

  const { data, error } = await supabase
    .from('campaign_characters')
    .upsert({ campaign_id: campaignId, character_id: characterId, char_role: charRole })
    .select()
    .single()
  return { data: data as CampaignCharacter | null, error }
}

/**
 * For each of the current user's characters that is assigned to a campaign,
 * which campaign it belongs to. Keyed by `character_id` for easy lookup in the
 * dashboard (a character may be assigned to at most one campaign — enforced by
 * the `uq_character_single_campaign` constraint).
 *
 * Scoped to my own characters via the `owner_id = auth.uid()` filter on the
 * joined `characters` row; the `members read` RLS policy further guarantees we
 * only see assignments in campaigns we belong to.
 */
export async function listMyCharacterAssignments(): Promise<
  Result<Record<string, CharacterAssignment>>
> {
  const { data: userData } = await supabase.auth.getUser()
  const userId = userData.user?.id
  if (!userId) return { data: {}, error: null }

  const { data, error } = await supabase
    .from('campaign_characters')
    .select('character_id, characters!inner(owner_id), campaigns(id, name)')
    .eq('characters.owner_id', userId)
  if (error || !data) return { data: null, error }

  type JoinRow = {
    character_id: string
    campaigns: { id: string; name: string } | null
  }

  const map: Record<string, CharacterAssignment> = {}
  for (const row of data as unknown as JoinRow[]) {
    if (row.campaigns) {
      map[row.character_id] = { campaignId: row.campaigns.id, campaignName: row.campaigns.name }
    }
  }
  return { data: map, error: null }
}

export async function unassignCharacter(
  campaignId: string,
  characterId: string,
): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase
    .from('campaign_characters')
    .delete()
    .eq('campaign_id', campaignId)
    .eq('character_id', characterId)
  return { error }
}

/** All characters assigned to a campaign, joined with the character row. */
export async function listCampaignCharacters(
  campaignId: string,
): Promise<Result<CampaignCharacterRow[]>> {
  const { data, error } = await supabase
    .from('campaign_characters')
    .select('char_role, added_at, characters(id, name, owner_id, data, updated_at)')
    .eq('campaign_id', campaignId)
    .order('added_at', { ascending: true })
  if (error || !data) return { data: null, error }

  type JoinRow = {
    char_role: CharRole
    characters: {
      id: string
      name: string
      owner_id: string
      data: CampaignCharacterRow['data']
      updated_at: string
    } | null
  }

  const rows: CampaignCharacterRow[] = (data as unknown as JoinRow[])
    .filter((row) => row.characters)
    .map((row) => {
      const c = row.characters!
      return {
        character_id: c.id,
        name: displayCharacterName(c.name), // Q6, display only
        owner_id: c.owner_id,
        char_role: row.char_role,
        data: c.data,
        updated_at: c.updated_at,
      }
    })
  return { data: rows, error: null }
}

/** The shareable invite link for a campaign's invite token (migration 034). */
export function inviteLink(token: string): string {
  return `${location.origin}/campaigns?join=${token}`
}

/**
 * Extract a campaign id from a pasted invite link, a plain URL, or a bare id, and say which one the
 * user entered: `'link'` when the input parses as a URL or contains `/` or `?` (a scheme-less link
 * such as `ordiria.app/campaigns?join=<id>` is parsed against a fixed base), else `'id'`.
 */
export function parseInvite(input: string): { id: string; kind: 'link' | 'id' } {
  const trimmed = input.trim()
  let url: URL
  try {
    url = new URL(trimmed)
  } catch {
    // Not an absolute URL: a bare id, unless it has a path or a query (a link without its scheme).
    if (!/[/?]/.test(trimmed)) return { id: trimmed, kind: 'id' }
    try {
      url = new URL(trimmed, 'https://invite.invalid')
    } catch {
      return { id: trimmed, kind: 'link' }
    }
  }
  const join = url.searchParams.get('join')
  if (join) return { id: join, kind: 'link' }
  // A path URL like `/campaigns/<id>`: the last non-empty segment is the id.
  const segments = url.pathname.split('/').filter(Boolean)
  return { id: segments.length ? segments[segments.length - 1]! : trimmed, kind: 'link' }
}
